import type { StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import buttonStyles from '@/styles/buttons.module.css'
import { useEffect, useRef, useState } from 'react'
import styles from './CampaignAssetsStep.module.css'

interface UploadedFile {
  id: string
  name: string
  size: number
  date: string
  type: string
  file: File
}

// Create a stable, order-independent signature of files
const computeFilesSignature = (fileList: File[]): string => {
  if (!fileList || fileList.length === 0) return 'EMPTY'
  const parts = fileList.map((f) => `${f.name}|${f.size}|${f.lastModified ?? ''}|${f.type}`).sort()
  return `${fileList.length}::${parts.join('||')}`
}

export const CampaignAssetsStep = ({ data, updateData }: StepProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateParentWithFiles = (updatedFiles: UploadedFile[]) => {
    const assetFiles = updatedFiles.map((f) => f.file)
    const sig = computeFilesSignature(assetFiles)
    updateData({
      assetFiles,
      assetFilesSignature: sig,
      assetFilesChanged: data.uploadedAssetsSignature ? data.uploadedAssetsSignature !== sig : true,
    })
  }

  // Initialize state from formData when component mounts or data changes
  useEffect(() => {
    if (data.assetFiles && data.assetFiles.length > 0 && files.length === 0) {
      const loadedFiles: UploadedFile[] = data.assetFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        date: formatDate(new Date()),
        type: file.type,
        file: file,
      }))
      setFiles(loadedFiles)

      // Ensure signature is set in parent on initial load
      const sig = computeFilesSignature(data.assetFiles)
      if (data.assetFilesSignature !== sig) {
        updateData({
          assetFilesSignature: sig,
          assetFilesChanged: data.uploadedAssetsSignature ? data.uploadedAssetsSignature !== sig : true,
        })
      }
    }
  }, [data.assetFiles, data.assetFilesSignature, data.uploadedAssetsSignature, files.length, updateData])

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round(bytes / k ** i)} ${sizes[i]}`
  }

  const formatDate = (date: Date): string => {
    const day = date.getDate()
    const month = date.toLocaleString('en-US', { month: 'short' })
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return 'PDF'
    if (fileType.includes('word') || fileType.includes('document')) return 'DOC'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'XLS'
    if (fileType.includes('image')) return 'IMG'
    return 'FILE'
  }

  const getFileIconColor = (fileType: string): string => {
    if (fileType.includes('pdf')) return '#E74C3C'
    if (fileType.includes('word') || fileType.includes('document')) return '#2E86DE'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '#27AE60'
    if (fileType.includes('image')) return '#9B59B6'
    return '#95A5A6'
  }

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return

    const newFiles: UploadedFile[] = Array.from(fileList).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      date: formatDate(new Date()),
      type: file.type,
      file: file,
    }))

    const updatedFiles = [...files, ...newFiles]
    setFiles(updatedFiles)

    updateParentWithFiles(updatedFiles)
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = files.filter((file) => file.id !== fileId)
    setFiles(updatedFiles)

    updateParentWithFiles(updatedFiles)
  }

  return (
    <div className={styles.Container}>
      <div
        className={`${styles.UploadArea} ${isDragging ? styles.Dragging : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.IconWrapper}>
          <img src="/emptyFolder.svg" alt="Upload" />
        </div>
        <div className={styles.TextAndButtonWrapper}>
          <p className={styles.Text}>Drag and drop or</p>
          <div>
            <button type="button" className={buttonStyles['button-primary']} onClick={handleButtonClick}>
              Upload File
            </button>
          </div>
        </div>
      </div>

      <div className={styles.FileInfo}>
        <p className={styles.SupportedFiles}>
          Supported files: <span>.jpg, .png, .svg, .pdf, .doc, .docx</span>
        </p>
        <p className={styles.MaxSize}>
          Maximum file size: <span>100MB</span>
        </p>
      </div>

      {files.length > 0 && (
        <div className={styles.FileList}>
          {files.map((file) => (
            <div key={file.id} className={styles.FileItem}>
              <div className={styles.FileIcon} style={{ backgroundColor: getFileIconColor(file.type) }}>
                {getFileIcon(file.type)}
              </div>
              <div className={styles.FileDetails}>
                <p className={styles.FileName}>{file.name}</p>
                <p className={styles.FileMetadata}>
                  {file.date} | {formatFileSize(file.size)}
                </p>
              </div>
              <button type="button" className={styles.RemoveButton} onClick={() => handleRemoveFile(file.id)} aria-label="Remove file">
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg,.pdf,.doc,.docx"
        multiple
        className={styles.HiddenInput}
      />
    </div>
  )
}
